import React, { useState, useEffect, useCallback } from 'react'
import { Search, User, Info } from 'lucide-react'
import {
  Button, Input, Label, Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
  Checkbox,
} from '../ui'
import {
  createPlayer, updatePlayer,
  getFamilias, createFamilia, assignPlayerToFamilia, renderAvisoClub,
  getPadronParaDuplicados,
} from '../../lib/supabase'
import {
  buscarPersonaPorTelefono, buscarPersonas, crearPersona, ligarPersonaAFamilia,
  responsablePrincipalDeFamilia,
} from '../../lib/services/personas'
import { apellidoPaterno } from '../../lib/nombres'
import {
  resolverResponsable, responsableQueSeReemplaza, responsableIgualAlDeLaFicha,
  familiaTieneOtroResponsable, partirSinPerder, soloDigitos, mismoTexto,
} from '../../lib/responsableResolver'
import { isValidCurp, isValidCurpChecksum, normalizeCurp, curpMatchesPerson } from '../../lib/curp'
import { isMinor } from '../../lib/age'
import { destinoConsentimiento } from '../../lib/consent'
import { buscarCoincidencias } from '../../lib/duplicados'
import { useOrganization } from '../../contexts/OrganizationContext'
import LegalDocLink from '../legal/LegalDocLink'
import SolicitarConsentimientoDialog from '../consent/SolicitarConsentimientoDialog'
import { useUnsavedGuard } from '../../hooks/useUnsavedGuard'

const ESTATUS_OPTIONS = [
  { value: 'Activo', label: 'Activo' },
  { value: 'Pendiente', label: 'Pendiente' },
  { value: 'Inactivo', label: 'Inactivo' },
  { value: 'Suspendido', label: 'Suspendido' },
  { value: 'Baja', label: 'Baja' },
]

const GENERO_OPTIONS = [
  { value: 'Varonil', label: 'Varonil' },
  { value: 'Femenil', label: 'Femenil' },
]

const initialForm = {
  nombres: '',
  apellidos: '',
  nickname: '',
  numero: '',
  genero: '',
  fecha_nacimiento: '',
  curp: '',
  telefono_jugador: '',
  tutor_nombres: '',
  tutor_apellidos: '',
  tutor_telefono: '',
  email_tutor: '',
  estatus: 'Activo',
}

const PlayerForm = ({ player = null, onSaved, onCancel }) => {
  const { org } = useOrganization()
  const guard = useUnsavedGuard({ onClose: () => onCancel?.() })
  const [form, setForm] = useState(initialForm)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [consentErr, setConsentErr] = useState(false)
  const [solicitarOpen, setSolicitarOpen] = useState(false)
  const [avisoConsent, setAvisoConsent] = useState(null)
  // Alta que quedo Pendiente por falta de consentimiento: no es un error, pero el
  // admin tiene que enterarse -- y desde aqui puede pedir la firma en el momento.
  const [pendienteConsent, setPendienteConsent] = useState(null)

  // Responsable de pago (familia)
  const [familias, setFamilias] = useState([])
  const [familiaSearch, setFamiliaSearch] = useState('')
  const [selectedFamilia, setSelectedFamilia] = useState(null)
  const [showFamiliaSearch, setShowFamiliaSearch] = useState(false)

  // El responsable como persona: quien ya existe con ese telefono (candidatos), a
  // quien se decidio ligar (personaLigada) y el telefono para el que ya se dijo
  // "es otra" -- para no volver a preguntar lo mismo en cada blur.
  const [candidatos, setCandidatos] = useState([])
  const [personaLigada, setPersonaLigada] = useState(null)
  const [telefonoDescartado, setTelefonoDescartado] = useState('')
  // Y lo mismo para el nombre: desde que se busca tambien por ahi, "es otra" tiene que
  // poder callar la sugerencia sin que la siguiente tecla la resucite.
  const [nombreDescartado, setNombreDescartado] = useState('')

  // Cual de los dos campos escribio el capturista, y por lo tanto que se esta buscando.
  // Empieza en null y eso importa: al abrir una ficha que ya existe los campos vienen
  // llenos, y buscar sobre lo que la ficha ya traia le pondria enfrente "esta persona
  // ya existe" a las 156 fichas que ya tienen persona ligada.
  const [campoTocado, setCampoTocado] = useState(null)
  // Por cuál de los dos aparecieron: la tarjeta no puede decir "este teléfono ya es de
  // X" cuando a X lo encontró el nombre y el teléfono todavía está vacío.
  const [origenCandidatos, setOrigenCandidatos] = useState('telefono')

  // La familia ya tiene responsable y lo capturado es otra persona: aqui queda quien
  // seria destronado, para preguntarlo. Y en `reemplazoAutorizado`, la respuesta --
  // guardada con a QUIEN se reemplaza, EN QUE familia y POR QUIEN, para que cambiar
  // cualquiera de las tres vuelva a preguntar.
  const [conflicto, setConflicto] = useState(null)
  const [reemplazoAutorizado, setReemplazoAutorizado] = useState(null)

  // El telefono que se tecleo y NO se va a guardar, porque el responsable ya existe
  // y su ficha no se toca aqui. Se dice con todas sus letras: descartarlo en silencio
  // es la misma perdida que este trabajo lleva cinco rondas persiguiendo.
  const [telefonoNoAplicado, setTelefonoNoAplicado] = useState('')

  // El guardado que se detuvo para avisar de ese teléfono. No es un error: es un
  // "mira esto antes de continuar", y el siguiente Guardar sigue de largo.
  const [detenidoPorTelefono, setDetenidoPorTelefono] = useState(false)

  // Padron de la org para avisar de altas repetidas mientras se captura.
  const [padron, setPadron] = useState([])

  // Jugador mayor de edad que responde por si mismo. NO se copian los datos (se
  // desactualizarian): se leen EN VIVO del propio jugador al mostrar y al guardar.
  const [tutorEsJugador, setTutorEsJugador] = useState(false)

  // El nombre completo del jugador ya no se captura: se arma de las partes. Sigue
  // existiendo porque 68 archivos lo leen y porque las heuristicas de duplicados y
  // de hermanos comparan sobre el texto completo.
  const nombreCompleto = `${form.nombres} ${form.apellidos}`.trim()

  // Quien responde, resuelto en un solo lugar: la casilla "responde por si mismo" y
  // la familia tienen que leer lo mismo, o una acaba guardando un campo vacio.
  const responsableNombre = tutorEsJugador
    ? nombreCompleto
    : `${form.tutor_nombres} ${form.tutor_apellidos}`.trim()
  const responsableTelefono = tutorEsJugador ? form.telefono_jugador : form.tutor_telefono
  const responsableApellidos = tutorEsJugador ? form.apellidos : form.tutor_apellidos

  // Apellidos = las dos últimas palabras del nombre, sin acentos ni mayúsculas.
  // Es la misma regla con la que se detectaron 21 grupos de hermanos en RAIDERS, y
  // es precisa justo porque exige LAS DOS: un solo "González" no dice nada en un
  // club donde hay veinte, pero "hernández saldivar" completo sí.
  const apellidosDe = (nombre) => {
    const partes = String(nombre || '')
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .toLowerCase().trim().split(/\s+/).filter(Boolean)
    return partes.length >= 2 ? partes.slice(-2).join(' ') : ''
  }

  // Familias cuyos jugadores comparten apellidos con el que se está registrando.
  // Sin esto, agrupar hermanos depende de que al admin se le ocurra buscar -- y en
  // RAIDERS no se le ocurrió 179 veces.
  const apellidosJugador = apellidosDe(nombreCompleto)
  const familiasHermanas = apellidosJugador
    ? familias.filter((f) =>
        (f.jugadores || []).some(
          (j) => j.id !== player?.id && apellidosDe(j.nombre_completo) === apellidosJugador
        )
      ).slice(0, 3)
    : []

  const isEditing = !!player

  // ¿El responsable quedó exactamente como lo traía la ficha?
  //
  // Gobierna dos cosas: no volver a tocar el modelo de personas cuando no hay nada
  // que resolver, y no exigirle a una edición requisitos que la ficha nunca cumplió.
  // En producción hay 28 responsables sin apellido ("Cecy", "Grisel", "Mamá") y 16
  // sin teléfono de 10 dígitos: exigirlos de forma retroactiva bloquearía corregir
  // una CURP o un dorsal en ~1 de cada 6 fichas del club.
  //
  // La comparación vive en responsableIgualAlDeLaFicha() y compara contra el nombre
  // YA PARTIDO: comparar contra `player.contacto` crudo hacía que recortar "Papá"
  // contara como una edición que nadie hizo. Son 5 fichas en producción, y una de
  // ellas -- Ximena Barajas -- terminaba renombrando a la responsable de la familia.
  const responsableSinCambios = isEditing && !personaLigada && responsableIgualAlDeLaFicha({
    nombreCapturado: responsableNombre,
    telefonoCapturado: responsableTelefono,
    emailCapturado: form.email_tutor,
    contactoFicha: player?.contacto,
    telefonoFicha: player?.telefono_contacto,
    emailFicha: player?.email_tutor,
  })

  // El alta sí los exige, y también la edición que toca al responsable: ahí sí hay
  // alguien capturando ese dato ahora mismo. Con una persona ya ligada no se exige
  // nada: sus campos van en solo lectura, así que pedir lo que no se puede escribir
  // dejaría el guardado bloqueado sin salida.
  const exigeResponsableCompleto = !responsableSinCambios && !personaLigada

  // El vínculo que importa para heredar familia es el de RESPONSABLE, y entre esos
  // el principal. El filtro por rol se queda aunque esta pantalla ya no escriba
  // ningún otro: `familia_personas.rol` sigue aceptando 'emergencia' y heredar
  // familia de un vínculo que no es de responsabilidad mudaría al jugador.
  const vinculoResponsableDe = (persona) => {
    const vinculos = (persona.familia_personas || []).filter((v) => v.familias)
    return vinculos.find((v) => v.rol === 'responsable' && v.es_principal)
      || vinculos.find((v) => v.rol === 'responsable')
      || null
  }

  // Jugadores que ya existen y se parecen al que se esta capturando. La CURP
  // repetida es un duplicado seguro y se bloquea; el nombre parecido puede ser un
  // hermano o un homonimo, asi que solo avisa.
  const coincidencias = buscarCoincidencias(
    { nombre: nombreCompleto, curp: form.curp }, padron, player?.id
  )
  const curpRepetida = coincidencias.find((c) => c.motivo === 'curp') || null
  const nombresParecidosEncontrados = coincidencias.filter((c) => c.motivo === 'nombre')

  // El teléfono del jugador solo se captura para mayores de edad (uso interno).
  const menorEdad = isMinor(form.fecha_nacimiento) === true

  // Load familias
  const loadFamilias = useCallback(async () => {
    const { data } = await getFamilias()
    setFamilias(data || [])
  }, [])

  useEffect(() => {
    loadFamilias()
  }, [loadFamilias])

  useEffect(() => {
    getPadronParaDuplicados().then(({ data }) => setPadron(data || []))
  }, [])

  useEffect(() => {
    if (player) {
      // Las columnas ya partidas mandan sobre partirNombre(): si alguien corrigió a
      // mano un apellido que la heurística había puesto mal, volver a partir el
      // completo tiraría justo esa corrección. partirNombre() solo entra cuando la
      // ficha todavía no tiene partes (fichas creadas fuera del backfill).
      const partesJugador = (player.nombres || player.apellidos)
        ? { nombres: player.nombres || '', apellidos: player.apellidos || '' }
        : partirSinPerder(player.nombre_completo)
      // QUIÉN RESPONDE POR ÉL SE LEE DE SU FAMILIA, no de su propia ficha.
      //
      // `jugadores.contacto` es una copia vieja del responsable, y nadie la actualiza
      // cuando se captura desde Fichas por completar: ahí el vínculo se crea contra la
      // FAMILIA. Por eso una ficha podía seguir diciendo "sin responsable" cuando su
      // familia ya tenía uno -- dos versiones del mismo dato, y una envejeciendo.
      //
      // `familia.responsable` ES la persona principal del vínculo. `contacto` queda de
      // respaldo para las fichas sin familia.
      const famDelJugador = player.familia_id
        ? (familias || []).find((f) => f.id === player.familia_id)
        : null
      const respDeLaFamilia = String(famDelJugador?.responsable?.nombre_completo || '').trim()
      const partesTutor = partirSinPerder(respDeLaFamilia || player.contacto)

      setForm({
        nombres: partesJugador.nombres,
        apellidos: partesJugador.apellidos,
        nickname: player.nickname || '',
        numero: player.numero || '',
        genero: player.genero || '',
        fecha_nacimiento: player.fecha_nacimiento || '',
        curp: player.curp || '',
        telefono_jugador: player.telefono_jugador || '',
        tutor_nombres: partesTutor.nombres,
        tutor_apellidos: partesTutor.apellidos,
        // Mismo criterio que el nombre: manda la familia, la ficha es el respaldo.
        tutor_telefono: famDelJugador?.responsable?.telefono || player.telefono_contacto || '',
        email_tutor: famDelJugador?.responsable?.email || player.email_tutor || '',
        estatus: player.estatus || 'Activo',
      })

      // Set existing familia
      if (player.familia_id) {
        const fam = familias.find((f) => f.id === player.familia_id)
        if (fam) setSelectedFamilia(fam)
      } else {
        setSelectedFamilia(null)
      }
    } else {
      setForm(initialForm)
      setSelectedFamilia(null)
    }
    setError(null)
    setTutorEsJugador(false)
    setShowFamiliaSearch(false)
    setFamiliaSearch('')
    setCandidatos([])
    setPersonaLigada(null)
    setTelefonoDescartado('')
    setNombreDescartado('')
    setCampoTocado(null)
    setConflicto(null)
    setReemplazoAutorizado(null)
    setTelefonoNoAplicado('')
    setDetenidoPorTelefono(false)
  }, [player, familias])

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }))
  }

  // Sin markDirty: Radix llama a este handler tambien cuando el valor cambia POR CODIGO
  // (al poblar la ficha), y eso ensuciaba el formulario sin que nadie tocara nada. Al
  // elegir de verdad, el <select> oculto del propio Select despacha un `change` que la
  // guarda si recoge, porque viene precedido del pointerdown sobre el disparador.
  const handleSelectChange = (field) => (value) => {
    setForm((prev) => ({ ...prev, [field]: value }))
  }

  // QUIEN YA EXISTE, MIENTRAS SE ESCRIBE.
  //
  // Antes solo se preguntaba al SALIR del campo del teléfono, y con 10 dígitos: para
  // cuando el capturista se enteraba de que esa persona ya estaba, ya había tecleado la
  // ficha entera. Ahora se busca desde el tercer carácter -- por teléfono o por nombre,
  // el que se esté escribiendo -- con debounce para no pegarle a la base en cada tecla.
  //
  // SUGIERE, NO DECIDE. Nada se liga aquí: se llena la tarjeta de candidatos, que ya
  // existe y trae las dos salidas ("es la misma persona" / "es otra"), y elige un
  // humano. Es la regla 5, y es lo que costó siete defectos aprender.
  //
  // Se busca SOLO el último campo tocado, y esto no es un detalle: buscar por los dos a
  // la vez hacía que el resultado del nombre borrara el del teléfono (o al revés) según
  // cuál respondiera primero. Y `campoTocado` empieza en null, así que abrir una ficha
  // que ya venía llena no dispara nada.
  useEffect(() => {
    if (!campoTocado) return
    if (personaLigada || tutorEsJugador) return

    const porTelefono = campoTocado === 'telefono'
    const tecleado = porTelefono ? responsableTelefono : responsableNombre
    // Ya contestó "es otra" para esto: no se le vuelve a preguntar lo mismo.
    if (porTelefono && soloDigitos(tecleado) === soloDigitos(telefonoDescartado) && telefonoDescartado) return
    if (!porTelefono && nombreDescartado && mismoTexto(tecleado, nombreDescartado)) return

    let vigente = true
    const t = setTimeout(async () => {
      const { data } = await buscarPersonas(tecleado, org?.id || null)
      // La respuesta de una búsqueda que el capturista ya dejó atrás no puede pisar la
      // pantalla: entre la tecla y la base pasa tiempo, y las respuestas no vuelven
      // necesariamente en orden.
      if (!vigente) return
      setCandidatos(data || [])
      setOrigenCandidatos(porTelefono ? 'telefono' : 'nombre')
    }, 275)
    return () => { vigente = false; clearTimeout(t) }
  }, [
    campoTocado, personaLigada, tutorEsJugador,
    responsableTelefono, responsableNombre, telefonoDescartado, nombreDescartado, org?.id,
  ])

  // El NOMBRE también identifica, y aquí es lo único que puede identificar: cuando
  // el responsable de la familia cambió de número, el teléfono nuevo no encuentra a
  // nadie y sólo el nombre delata que es él.
  //
  // Se resuelve al salir del campo, no al guardar, y esa es toda la diferencia: el
  // alta ya no reescribe a una persona que existe, así que reconocerlo al guardar
  // significaba descartar en silencio el teléfono que el admin acababa de teclear.
  // Reconocerlo aquí pone los campos en sólo lectura con su pie -- el admin ve ANTES
  // de guardar que ese número no se va a aplicar, y a dónde ir a cambiarlo.
  const reconocerResponsableDeLaFamilia = async () => {
    const familia = selectedFamilia?.id || player?.familia_id || null
    if (personaLigada || tutorEsJugador || !familia) return
    const nombre = `${form.tutor_nombres} ${form.tutor_apellidos}`.trim()
    if (!nombre) return
    const { data: principal } = await responsablePrincipalDeFamilia(familia)
    if (principal?.id && mismoTexto(nombre, principal.nombre_completo)) usarPersona(principal)
  }

  // Cambiar el teléfono invalida lo que se había decidido sobre el anterior: si ya
  // no es el mismo número, tampoco es necesariamente la misma persona.
  const handleTelefonoResponsable = (e) => {
    setForm((prev) => ({ ...prev, tutor_telefono: e.target.value }))
    setCampoTocado('telefono')
    setCandidatos([])
    setPersonaLigada(null)
    setTelefonoDescartado('')
    setConflicto(null)
    setTelefonoNoAplicado('')
    setDetenidoPorTelefono(false)
  }

  // El nombre también busca, y por lo mismo que el teléfono: cuando el responsable
  // cambió de número, el número nuevo no encuentra a nadie y sólo el nombre lo delata.
  // Escribirlo tampoco decide nada -- llena la tarjeta y ya.
  const handleNombreResponsable = (campo) => (e) => {
    setForm((prev) => ({ ...prev, [campo]: e.target.value }))
    setCampoTocado('nombre')
    setCandidatos([])
    setNombreDescartado('')
  }

  // "Es la misma persona" / "Deja a quien ya responde": se liga la persona que ya
  // existe y el formulario pasa a MOSTRAR sus datos, no a capturarlos -- el alta no
  // escribe sobre una persona que ya existe. NO se fusionan familias: si la persona
  // ya tiene una, este jugador se suma a ella, pero ninguna otra familia se mueve,
  // así que no se mueve ningún saldo.
  //
  // Se traen sus datos, pero un campo vacío NUNCA borra lo que ya había: en PROD solo
  // 6 de 136 personas tienen correo, así que copiar el suyo a ciegas VACIABA el correo
  // de la ficha -- y `updatePlayer` sí escribe esa columna. Por eso los `prev.X ||`.
  //
  // El teléfono es el único que manda sobre lo tecleado, porque es el que acabaría en
  // `jugadores.telefono_contacto` con el nombre de otra persona al lado. Y si lo
  // tecleado era otro número, se DICE (telefonoNoAplicado): descartarlo en silencio es
  // la pérdida que este trabajo lleva cinco rondas persiguiendo.
  const usarPersona = (persona) => {
    guard.markDirty()
    const telTecleado = soloDigitos(responsableTelefono)
    const telSuyo = soloDigitos(persona.telefono)
    setTelefonoNoAplicado(
      telTecleado && telSuyo && telTecleado !== telSuyo ? responsableTelefono.trim() : ''
    )
    const vinculo = vinculoResponsableDe(persona)
    setPersonaLigada({
      id: persona.id,
      nombre: persona.nombre_completo || `${persona.nombres || ''} ${persona.apellidos || ''}`.trim(),
      familiaId: vinculo?.familias?.id || null,
      familiaNombre: vinculo?.familias?.nombre || null,
      // Sus vínculos tal cual: son los que dicen de qué familia ya es responsable, y
      // por lo tanto a cuál se suma este jugador en vez de crear una segunda.
      familia_personas: persona.familia_personas || [],
    })
    setForm((prev) => ({
      ...prev,
      tutor_nombres: persona.nombres || prev.tutor_nombres,
      tutor_apellidos: persona.apellidos || prev.tutor_apellidos,
      tutor_telefono: persona.telefono || prev.tutor_telefono,
      email_tutor: prev.email_tutor || persona.email || '',
    }))
    setCandidatos([])
    setConflicto(null)
  }

  // "Es otra": responde la pregunta 1 (identidad). Para este teléfono ya se contestó,
  // no se reusa a nadie y al guardar se crea la persona nueva.
  const esOtraPersona = () => {
    guard.markDirty()
    // Se descartan los DOS lados de la pregunta, no sólo el teléfono: ahora la tarjeta
    // también puede haber aparecido por el nombre, y callarla por un lado mientras el
    // otro la vuelve a levantar en la siguiente tecla es no callarla.
    setTelefonoDescartado(soloDigitos(responsableTelefono))
    setNombreDescartado(responsableNombre.trim())
    setCandidatos([])
    // Cambió la identidad, así que la pregunta 2 hay que rehacerla: dejar su tarjeta
    // en pantalla mostraba las dos a la vez, contestando cosas distintas.
    setConflicto(null)
  }

  // "Cambiar el responsable": responde la pregunta 2, que es OTRA -- "¿es la misma
  // persona?" nunca fue "¿reemplazo al responsable de esta familia?", y confundirlas
  // fue el Crítico. Se guarda a quién se reemplaza, en qué familia y por quién: si el
  // admin cambia cualquiera de las tres, se le vuelve a preguntar.
  const autorizarReemplazo = () => {
    if (!conflicto?.saliente) return
    guard.markDirty()
    setReemplazoAutorizado({
      familiaId: conflicto.familiaId || null,
      saliente: conflicto.saliente,
      nombre: responsableNombre,
    })
    setConflicto(null)
  }

  // "Dejar a X": conserva al responsable que la familia ya tiene, ligándolo. Desde la
  // Fase 3 el responsable de una familia ES su vínculo principal: no hay otra forma.
  const dejarAlSaliente = () => {
    if (conflicto?.persona?.id) usarPersona(conflicto.persona)
  }

  // "Responsable de Familia Ruiz — Luis · Diego": el dato con el que un humano
  // decide si es la misma persona. Nunca el id.
  const describirPersona = (persona) => {
    const vinculo = vinculoResponsableDe(persona)
      || (persona.familia_personas || []).find((v) => v.familias)
      || null
    if (!vinculo) return 'Todavía no pertenece a ninguna familia.'
    const jugadores = (vinculo.familias.jugadores || []).map((j) => j.nombre_completo).filter(Boolean)
    // `familia_personas` es solo de pagos y contacto: el tutor legal cuelga del jugador,
    // en `jugador_personas`, y aquí no aparece. Solo hay que distinguir 'emergencia',
    // que sí vive aquí y no es quien paga.
    const ROLES = { emergencia: 'Contacto de emergencia' }
    const rol = ROLES[vinculo.rol] || 'Responsable'
    return `${rol} de ${vinculo.familias.nombre}${jugadores.length ? ` — ${jugadores.join(' · ')}` : ''}`
  }

  const filteredFamilias = familias.filter((f) => {
    if (!familiaSearch) return true
    const t = familiaSearch.toLowerCase()
    return f.nombre?.toLowerCase().includes(t) || f.responsable?.nombre_completo?.toLowerCase().includes(t)
  }).slice(0, 8)

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError(null)
    setConsentErr(false)

    if (!form.nombres.trim() || !form.apellidos.trim()) {
      setError('Captura el nombre y los apellidos del jugador, cada uno en su campo.')
      return
    }

    // CURP: validar formato si se capturó; y exigirla si el jugador será Activo.
    if (form.curp.trim() && !isValidCurp(form.curp)) {
      setError('La CURP no tiene un formato válido (18 caracteres).')
      return
    }
    if (form.estatus === 'Activo' && !isValidCurp(form.curp)) {
      setError('Captura una CURP válida para guardar al jugador como Activo.')
      return
    }
    // Dígito verificador: si el formato es correcto pero el dígito no cuadra,
    // la CURP está mal escrita.
    if (form.curp.trim() && isValidCurp(form.curp) && !isValidCurpChecksum(form.curp)) {
      setError('La CURP no es válida: el dígito verificador no coincide. Revisa que esté bien escrita.')
      return
    }
    // Una CURP repetida dentro del club es la misma persona dada de alta dos veces:
    // la CURP identifica a UN individuo. No es un homónimo, es un duplicado.
    if (curpRepetida) {
      setError(`Esa CURP ya está registrada en ${curpRepetida.nombre_completo}. Si es la misma persona, edita esa ficha en lugar de crear otra.`)
      return
    }
    // Con CURP, la fecha de nacimiento y el género son obligatorios.
    if (form.curp.trim() && (!form.fecha_nacimiento || !form.genero)) {
      setError('Captura la fecha de nacimiento y el género (obligatorios cuando hay CURP).')
      return
    }
    // Un menor sin tutor localizable es un jugador al que nunca se le podra pedir
    // el consentimiento: no hay a donde mandar el enlace ni el codigo. Hoy 58 de
    // 207 jugadores estan asi, y cada alta nueva sin contacto agranda ese hueco.
    // El responsable es obligatorio SIEMPRE, no solo para menores: toda ficha
    // pertenece a una familia, y una familia sin responsable no sirve para cobrar,
    // ni para el portal, ni para pedir el consentimiento. En un mayor de edad el
    // responsable puede ser el mismo jugador -- pero hay que decirlo.
    //
    // Las dos exigencias nuevas (apellidos y teléfono de 10 dígitos) NO son
    // retroactivas: solo aplican al alta y a la edición que toca al responsable. Una
    // edición que no lo toca pasa con lo que ya había, o corregir un dorsal quedaría
    // bloqueado en las 28 fichas cuyo responsable no tiene apellido.

    // La edad no dice QUIÉN firma -- eso sale de quién es el responsable -- pero sí
    // prohíbe: un menor no puede ser su propio responsable. La casilla se oculta para
    // menores, y esto atrapa el caso en que la fecha de nacimiento se capturó después
    // de marcarla, cuando `isMinor` todavía devolvía null.
    if (tutorEsJugador && menorEdad) {
      setError('Un menor de edad no puede ser su propio responsable: captura a su padre, madre o tutor.')
      return
    }
    if (exigeResponsableCompleto) {
      if (!tutorEsJugador && (!form.tutor_nombres.trim() || !form.tutor_apellidos.trim())) {
        setError(menorEdad
          ? 'Captura el nombre y los apellidos del padre, madre o tutor: del primer apellido sale el nombre de la familia.'
          : 'Captura al responsable con nombre y apellidos, o marca que el jugador responde por sí mismo.')
        return
      }
      if (soloDigitos(responsableTelefono).length < 10) {
        setError(tutorEsJugador
          ? 'Captura el teléfono del jugador a 10 dígitos: es a donde se le manda el enlace del consentimiento.'
          : 'Captura el teléfono del responsable a 10 dígitos: es a donde se manda el enlace del consentimiento, y es lo que evita registrar dos veces a la misma familia.')
        return
      }
    } else if (!tutorEsJugador && !form.tutor_nombres.trim() && !form.tutor_apellidos.trim()) {
      setError('Captura al responsable, o marca que el jugador responde por sí mismo.')
      return
    }
    if (form.email_tutor.trim() && !/\S+@\S+\.\S+/.test(form.email_tutor.trim())) {
      setError('El correo del tutor no tiene un formato válido.')
      return
    }

    setDetenidoPorTelefono(false)
    setSaving(true)
    try {
      // 0. EL RESPONSABLE. Dos preguntas distintas, resueltas por separado:
      //
      //      1. ¿QUIEN es esta persona?         -> resolverResponsable()
      //      2. ¿REEMPLAZO al que ya está ahí?  -> hayQuePreguntarPorElReemplazo()
      //
      // Estuvieron enredadas en una sola respuesta, y ahí se coló el Crítico: cada
      // camino que resolvía la identidad por su cuenta (la tarjeta de candidatos, la
      // coincidencia por teléfono, cambiar de familia después de ligar) se saltaba el
      // control y destronaba al responsable que la familia ya tenía. Ahora la 2 es UN
      // control, evaluado contra la familia FINAL, y los tres caminos pasan por él.
      //
      // Todo esto es solo lectura y va a propósito ANTES de guardar al jugador: si
      // hace falta un humano, se para aquí sin haber escrito nada.
      //
      // Dos familias distintas, y la diferencia importa:
      //  - `familiaId` es la de la PANTALLA. Es la única que puede MUDAR al jugador
      //    (`assignPlayerToFamilia` más abajo).
      //  - `familiaDelResponsable` es donde vive el responsable que se está
      //    corrigiendo: la de la pantalla, o si el admin la despejó con "Cambiar", la
      //    que el jugador tiene EN LA BASE. Sin esto la corrección no aterriza en
      //    ningún lado: la persona se crea huérfana, la familia real conserva al
      //    responsable viejo, la ficha muestra al nuevo y el modal se cierra como si
      //    todo hubiera salido bien.
      let familiaId = selectedFamilia?.id || personaLigada?.familiaId || null
      const familiaDelJugador = isEditing ? (player?.familia_id || null) : null
      let familiaDelResponsable = familiaId || familiaDelJugador

      const nombresResp = tutorEsJugador ? form.nombres : form.tutor_nombres
      const apellidosResp = tutorEsJugador ? form.apellidos : form.tutor_apellidos
      const nombreResp = `${nombresResp} ${apellidosResp}`.trim()

      let decision = null
      let personaResuelta = null

      // PREGUNTA 0. El jugador que responde por sí mismo y se suma a una familia que
      // YA tiene a otra persona respondiendo entra como un hijo más: la familia es de
      // pagos, quien paga sigue siendo quien ya estaba, y él sigue firmando por sí
      // mismo. No hay identidad que resolver ni nadie a quien reemplazar, así que las
      // preguntas 1 y 2 no se hacen y no se crea ni se liga ninguna persona.
      //
      // Sin esto su propio nombre contaba como "el responsable capturado", la pregunta
      // 2 veía a la mamá de sus hermanos y ofrecía destronarla. Se eligió eso, y los
      // dos menores se quedaron sin su responsable (2026-09-08).
      let entraComoHijo = false
      if (tutorEsJugador && !responsableSinCambios && familiaDelResponsable) {
        const { data: principal } = await responsablePrincipalDeFamilia(familiaDelResponsable)
        entraComoHijo = familiaTieneOtroResponsable({
          responsableActual: principal,
          nombreCapturado: nombreResp,
        })
      }

      // En una edición no se vuelve a tocar el modelo de personas si el responsable
      // quedó igual: `personas` no tiene índice único por teléfono, así que crear
      // una en cada guardado dejaría una fila nueva por cada edición de la misma
      // ficha. Los responsables de antes ya tienen su persona (backfill de
      // 20260825c_migrar_responsables.sql).
      if (!responsableSinCambios && !entraComoHijo) {
        // Quién responde hoy por esta familia. Sirve para las dos preguntas: para
        // reconocerlo si resulta ser el mismo, y para no reemplazarlo a la callada si
        // no lo es.
        let responsableActual = null
        if (familiaDelResponsable) {
          ;({ data: responsableActual } = await responsablePrincipalDeFamilia(familiaDelResponsable))
        }

        let candidatosPorTelefono = []
        if (!personaLigada && soloDigitos(responsableTelefono) !== telefonoDescartado) {
          const { data: yaExisten } = await buscarPersonaPorTelefono(responsableTelefono, org?.id || null)
          candidatosPorTelefono = yaExisten || []
        }

        // Pregunta 1: ¿quién es esta persona?
        decision = resolverResponsable({
          personaLigada,
          principalActual: responsableActual,
          candidatosPorTelefono,
          nombreCapturado: nombreResp,
          telefonoCapturado: responsableTelefono,
          telefonoDescartado,
        })

        // Reusar a una persona no puede crear una SEGUNDA familia: si ya es
        // responsable de una sola, este jugador se suma a ESA -- es lo que habría
        // hecho la tarjeta si el admin la hubiera usado, y nada lo obliga a usarla.
        // Se resuelve aquí, y no más abajo, porque la pregunta 2 tiene que evaluarse
        // contra la familia FINAL: comprobarla contra la de la pantalla es justo lo
        // que dejaba pasar el camino de "bloquear los campos y luego cambiar de
        // familia".
        if (!familiaDelResponsable && decision.familiaSugerida) {
          familiaDelResponsable = decision.familiaSugerida
          familiaId = decision.familiaSugerida
          ;({ data: responsableActual } = await responsablePrincipalDeFamilia(familiaDelResponsable))
        }

        // Pregunta 2: ¿reemplazo al que ya está ahí? UN control, los tres caminos. El
        // responsable de la familia es su vínculo principal; sin vínculo no hay a quién
        // destronar.
        //
        // SU LÍMITE, dicho: `familias` viene de getFamilias(), que filtra activo=true.
        // Una familia archivada no se mira, así que ese caso NO está cubierto. Hoy no
        // es alcanzable (PROD: 23 familias así contando archivadas, 3 activas, y 0
        // jugadores vivos en familia archivada), pero si eso cambia, este control no
        // las ve.
        const filaDeLaFamilia = familias.find((f) => f.id === familiaDelResponsable) || null
        const saliente = responsableQueSeReemplaza({
          responsableActual,
          personaId: decision.personaId,
          familiaId: familiaDelResponsable,
          nombreCapturado: nombreResp,
          autorizacion: reemplazoAutorizado,
        })
        if (saliente) {
          // De qué OTRA familia responde ya el entrante. El admin cree que autoriza un
          // reemplazo; sin esto no ve que autoriza además un acoplamiento permanente:
          // corregirle los datos a esa persona desde Pagos > Familias cambia las dos.
          const familiaDelEntrante = personaLigada?.familiaId || decision.familiaSugerida || null
          setConflicto({
            saliente,
            persona: responsableActual,
            familiaId: familiaDelResponsable,
            familiaDelEntrante: familiaDelEntrante && familiaDelEntrante !== familiaDelResponsable
              ? (familias.find((f) => f.id === familiaDelEntrante)?.nombre || null)
              : null,
            // Los jugadores de la familia, para que se vea de quién se habla.
            jugadores: (filaDeLaFamilia?.jugadores || [])
              .map((j) => j.nombre_completo).filter(Boolean),
          })
          setSaving(false)
          return
        }

        // 'usar' sin haber pasado por la tarjeta: es quien ya responde por la familia,
        // reconocido por el NOMBRE. Su ficha NO se toca, así que la del jugador tiene
        // que quedar con los datos de esa persona y no con lo tecleado -- si no,
        // `jugadores.telefono_contacto` guarda un número que el sistema no usa
        // (el consentimiento va al teléfono de la persona, que sigue siendo el de ella).
        if (decision.accion === 'usar' && !personaLigada) {
          personaResuelta = [responsableActual, ...candidatosPorTelefono]
            .find((p) => p?.id && p.id === decision.personaId) || null

          // Y si lo tecleado era OTRO número, se para y se DICE. Descartarlo aquí en
          // silencio es el mismo modo de falla de siempre por una puerta más angosta:
          // basta con pulsar Enter --que no dispara blur-- o con que el clic en Guardar
          // le gane la carrera al blur, que es asíncrono y viaja a la base.
          const telSuyo = soloDigitos(personaResuelta?.telefono)
          if (telSuyo && telSuyo !== soloDigitos(responsableTelefono)) {
            usarPersona(personaResuelta)
            setDetenidoPorTelefono(true)
            setSaving(false)
            return
          }
        }
      }

      // 1. Crear/actualizar jugador
      // El teléfono del jugador no se conserva para menores (uso interno, minimización).
      const payload = {
        ...form,
        nombres: form.nombres.trim(),
        apellidos: form.apellidos.trim(),
        // Las partes son la fuente; el completo va explícito de todos modos, para
        // que la fila quede bien aunque el trigger que lo arma falte en un entorno.
        nombre_completo: nombreCompleto,
        curp: normalizeCurp(form.curp),
        telefono_jugador: menorEdad ? '' : form.telefono_jugador,
        // `contacto` en jugadores es un solo texto: aquí se vuelve a juntar. El
        // jugador mayor de edad es su propio responsable, y responsableNombre ya
        // resuelve ese caso. Si el responsable es una persona que ya existía, manda
        // ELLA: su ficha no se reescribe, así que la del jugador tampoco puede
        // guardar otra versión de sus datos.
        tutor_nombre: personaResuelta?.nombre_completo || responsableNombre,
        tutor_telefono: String(personaResuelta ? personaResuelta.telefono || '' : responsableTelefono).trim(),
      }
      let result
      if (isEditing) {
        result = await updatePlayer(player.id, payload)
      } else {
        result = await createPlayer(payload)
      }

      if (result.error) throw result.error

      const playerId = result.data?.id || player?.id

      // 2. El responsable (persona), su familia y el vínculo -- DESPUÉS del jugador.
      //
      // Estaba al revés: se creaba la familia y luego se guardaba el jugador. Si el
      // guardado fallaba -- CURP repetida, un campo obligatorio -- la familia quedaba
      // huérfana, y cada reintento creaba otra. En RAIDERS quedaron 14 familias
      // "Familia guevara" vacías de un solo día así.
      //
      // Y dentro del bloque el orden también importa: persona, familia, vínculo. Es
      // el trigger trg_vinculo_espejo_familia (AFTER INSERT en familia_personas) el
      // que vuelve a esa persona el responsable de la familia; si se liga antes de que
      // exista la familia no hay fila que actualizar y la familia queda sin
      // responsable -- invisible para PagosFamilias y para el consentimiento, que
      // leen tutor_*.
      if (decision) {
        try {
          let personaId = decision.personaId

          // Aquí solo se CREA. 'usar' no tiene rama: se usa el id y no se le escribe
          // un solo campo. Esta pantalla llegó a actualizar a la persona elegida, y
          // de ahí salieron los cuatro Críticos: corregir una ficha le cambiaba el
          // destino del código a una familia que nadie abrió. Corregir a una persona
          // es de Pagos > Familias.
          if (decision.accion === 'crear') {
            const { data: nuevaPersona } = await crearPersona({
              nombres: nombresResp,
              apellidos: apellidosResp,
              telefono: responsableTelefono,
              email: form.email_tutor,
              orgId: org?.id || null,
            })
            personaId = nuevaPersona?.id || null
          }

          // La familia se crea SOLA, con el apellido PATERNO del responsable.
          //
          // Antes era un paso aparte y opcional, y por eso 179 de 206 jugadores de
          // RAIDERS quedaron sin ninguna. Y el nombre salía de la última palabra, que
          // en México es el materno: el mismo responsable capturado con uno o dos
          // apellidos generaba dos familias distintas.
          //
          // También al EDITAR, porque en producción hay 10 fichas sin familia y sin
          // esto una edición les creaba la persona y no tenía dónde ligarla. Pero la
          // condición mira `familiaDelResponsable`, no la pantalla: si el admin
          // despejó la familia con "Cambiar" y el jugador YA tenía una, no se crea
          // ninguna -- crearla lo mudaría, perdiendo el token, el PIN y el estado de
          // cuenta de su familia real.
          if (!familiaDelResponsable) {
            const { data: newFam } = await createFamilia({
              nombre: `Familia ${apellidoPaterno(responsableApellidos)}`.trim(),
            })
            familiaId = newFam?.id || null
            familiaDelResponsable = familiaId
          }

          if (familiaDelResponsable && personaId) {
            // Siempre principal, sin excepción: una familia tiene UN responsable, y
            // llegar aquí ya significa que nadie iba a ser destronado, o que un
            // humano autorizó el reemplazo (hayQuePreguntarPorElReemplazo, arriba).
            // El RPC le quita el puesto al anterior en la misma sentencia.
            //
            // El índice único parcial (familia_id, rol) WHERE es_principal NO alcanza
            // como control: impide dos principales en UNA familia, pero no impide que
            // una persona sea principal de VARIAS -- verificado en QA, ligar al mismo
            // en F1 y F2 pasa sin error. Lo que protege al responsable de la familia
            // es la pregunta 2, no la base.
            await ligarPersonaAFamilia({
              familiaId: familiaDelResponsable, personaId, rol: 'responsable',
            })
          }
        } catch (personaErr) {
          // Si esto falla no se rompe el alta: el jugador ya está guardado y la
          // familia se puede crear después. Perder al jugador por no poder agruparlo
          // sería el peor intercambio posible.
          console.error('[PlayerForm] responsable', personaErr)
        }
      }

      // 3. Asignar familia si hay
      if (familiaId) {
        await assignPlayerToFamilia(playerId, familiaId)
      }

      guard.resetDirty()
      if (!isEditing && result.aviso) {
        setPendienteConsent({ texto: result.aviso, jugadorId: result.data?.id })
        return
      }
      onSaved?.(result.data)
    } catch (err) {
      console.error('[PlayerForm]', err)
      setError(err?.message || 'Error al guardar jugador')
      if (err?.code === 'CONSENT_REQUIRED') setConsentErr(true)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="mx-auto w-full max-w-2xl">
        <form onSubmit={handleSubmit} {...guard.dirtyProps} className="space-y-6">
          {pendienteConsent && (
            <div className="space-y-2 rounded-md bg-amber-50 p-3 text-sm text-amber-900">
              <p>{pendienteConsent.texto}</p>
              <div className="flex flex-wrap gap-2">
                <Button type="button" variant="outline" size="sm" className="bg-background"
                  onClick={() => setSolicitarOpen(true)}>
                  Solicitar consentimiento
                </Button>
                <Button type="button" variant="ghost" size="sm" onClick={() => onCancel?.()}>
                  Cerrar
                </Button>
              </div>
            </div>
          )}
          {avisoConsent && (
            <div className="rounded-md bg-emerald-50 p-3 text-sm text-emerald-900">
              {avisoConsent}
            </div>
          )}
          {error && (
            <div className="p-3 rounded-md bg-destructive/10 text-destructive text-sm space-y-2">
              <p>{error}</p>
              {consentErr && isEditing && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="bg-background"
                  onClick={() => setSolicitarOpen(true)}
                >
                  Solicitar consentimiento
                </Button>
              )}
            </div>
          )}

          {/* 1 · El jugador */}
          <fieldset className="space-y-4">
            <legend className="text-sm font-semibold text-foreground border-b pb-1 w-full">
              1 · El jugador
            </legend>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="nombres">Nombre(s) *</Label>
                <Input
                  id="nombres"
                  value={form.nombres}
                  onChange={handleChange('nombres')}
                  placeholder="Ana Sofía"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="apellidos">Apellidos *</Label>
                <Input
                  id="apellidos"
                  value={form.apellidos}
                  onChange={handleChange('apellidos')}
                  placeholder="Paterno y materno"
                  required
                />
              </div>

              {nombresParecidosEncontrados.length > 0 && (
                <div className="sm:col-span-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-xs dark:border-amber-800 dark:bg-amber-950/40">
                  <p className="font-medium text-amber-900 dark:text-amber-200">
                    Ya hay {nombresParecidosEncontrados.length === 1 ? 'un jugador' : 'jugadores'} con un nombre casi igual:
                  </p>
                  <ul className="mt-1.5 space-y-1 text-amber-800 dark:text-amber-300">
                    {nombresParecidosEncontrados.map((j) => (
                      <li key={j.id}>
                        <span className="font-medium">{j.nombre_completo}</span>
                        {j.fecha_nacimiento && <> · {new Date(`${j.fecha_nacimiento}T12:00:00`).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' })}</>}
                        {j.estatus && <> · {j.estatus}</>}
                      </li>
                    ))}
                  </ul>
                  <p className="mt-1.5 text-amber-700 dark:text-amber-400">
                    Si es la misma persona, edita esa ficha. Si es otra, continúa.
                  </p>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="curp">CURP{form.estatus === 'Activo' ? ' *' : ''}</Label>
                <Input
                  id="curp"
                  value={form.curp}
                  onChange={handleChange('curp')}
                  placeholder="CURP del jugador"
                  maxLength={18}
                  className="uppercase"
                  aria-invalid={form.curp.trim() !== '' && !isValidCurp(form.curp)}
                />
                {form.curp.trim() !== '' && !isValidCurp(form.curp) && (
                  <p className="text-xs text-destructive">CURP invalida (18 caracteres).</p>
                )}
                {form.estatus === 'Activo' && !form.curp.trim() && (
                  <p className="text-xs text-muted-foreground">
                    Obligatoria para activar. Sin ella el jugador queda Pendiente.
                  </p>
                )}
                {form.curp.trim() !== '' && isValidCurp(form.curp) && !isValidCurpChecksum(form.curp) && (
                  <p className="text-xs text-destructive">El dígito verificador de la CURP no coincide.</p>
                )}
                {form.curp.trim() !== '' && isValidCurpChecksum(form.curp)
                  && curpMatchesPerson(form.curp, { fecha_nacimiento: form.fecha_nacimiento, genero: form.genero }).fechaOk === false && (
                  <p className="text-xs text-amber-600">La fecha de nacimiento de la CURP no coincide con la capturada.</p>
                )}
                {form.curp.trim() !== '' && isValidCurpChecksum(form.curp)
                  && curpMatchesPerson(form.curp, { fecha_nacimiento: form.fecha_nacimiento, genero: form.genero }).sexoOk === false && (
                  <p className="text-xs text-amber-600">El sexo de la CURP no coincide con el género capturado.</p>
                )}
                {curpRepetida && (
                  <p className="text-xs text-destructive">
                    Esa CURP ya está registrada en <strong>{curpRepetida.nombre_completo}</strong>.
                    La CURP identifica a una sola persona: edita esa ficha en lugar de crear otra.
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="fecha_nacimiento">Fecha de nacimiento</Label>
                <Input
                  id="fecha_nacimiento"
                  type="date"
                  value={form.fecha_nacimiento}
                  onChange={handleChange('fecha_nacimiento')}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="genero">Género</Label>
                <Select value={form.genero} onValueChange={handleSelectChange('genero')}>
                  <SelectTrigger id="genero">
                    <SelectValue placeholder="Seleccionar" />
                  </SelectTrigger>
                  <SelectContent>
                    {GENERO_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="numero">Número (club)</Label>
                <Input
                  id="numero"
                  value={form.numero}
                  onChange={handleChange('numero')}
                  placeholder="Opcional"
                  type="number"
                  min="0"
                  max="999"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="nickname">Nickname</Label>
                <Input
                  id="nickname"
                  value={form.nickname}
                  onChange={handleChange('nickname')}
                  placeholder="Opcional"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="estatus">Estatus</Label>
                <Select value={form.estatus} onValueChange={handleSelectChange('estatus')}>
                  <SelectTrigger id="estatus">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ESTATUS_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {!menorEdad && (
                <div className="space-y-2 sm:col-span-2">
                  <Label htmlFor="telefono_jugador">Teléfono del jugador</Label>
                  <Input
                    id="telefono_jugador"
                    value={form.telefono_jugador}
                    onChange={handleChange('telefono_jugador')}
                    placeholder="10 dígitos"
                    type="tel"
                  />
                  <p className="text-xs text-muted-foreground">Uso interno. No se comparte con terceros.</p>
                </div>
              )}
            </div>
            {menorEdad && (
              <p className="text-xs text-muted-foreground">
                El teléfono del jugador no se captura para menores de edad.
              </p>
            )}
          </fieldset>

          {/* 2 · Responsable. Aquí vive todo lo que cuelga de él: sus datos y de qué
              familia es el jugador. Estaban en bloques distintos y era la misma
              persona pedida dos veces. */}
          <fieldset className="space-y-4">
            <legend className="text-sm font-semibold text-foreground border-b pb-1 w-full">
              2 · Responsable
            </legend>
            <p className="text-xs text-muted-foreground">
              Por aquí se pide el consentimiento: hace falta su nombre y su teléfono. Sin
              ellos no hay a dónde mandárselo, y el jugador no se podrá activar.
            </p>

            {/* Un jugador mayor de edad responde por si mismo. Mismo patron que las
                otras dos casillas del formulario: no se copian los datos, se leen en
                vivo del propio jugador. Sigue visible si ya estaba marcada aunque la
                fecha de nacimiento lo vuelva menor, o quedaria marcada y escondida,
                sin forma de desmarcarla. */}
            {(!menorEdad || tutorEsJugador) && (
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox
                  checked={tutorEsJugador}
                  onCheckedChange={(v) => { guard.markDirty(); setTutorEsJugador(!!v) }}
                />
                El jugador responde por sí mismo
              </label>
            )}

            {/* El teléfono va primero porque es lo que identifica: desde el tercer
                dígito se va diciendo quién ya lo tiene, y el resto se llena solo si el
                capturista confirma que es alguien que ya existe. */}
            <div className="space-y-2">
              <Label htmlFor="tutor_telefono">Teléfono del responsable *</Label>
              <Input
                id="tutor_telefono"
                value={tutorEsJugador ? form.telefono_jugador : form.tutor_telefono}
                onChange={handleTelefonoResponsable}
                placeholder="10 dígitos"
                type="tel"
                disabled={tutorEsJugador}
              />
            </div>

            {candidatos.length > 0 && (
              <div className="rounded-lg border bg-muted/40 p-4 space-y-3">
                {candidatos.map((persona) => (
                  <div key={persona.id} className="flex items-start gap-2.5">
                    <User className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold">
                        {origenCandidatos === 'nombre'
                          ? `${persona.nombre_completo} ya está registrado`
                          : `Este teléfono ya es de ${persona.nombre_completo}`}
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5">{describirPersona(persona)}</p>
                    </div>
                    {candidatos.length > 1 && (
                      <Button type="button" size="sm" onClick={() => usarPersona(persona)}>
                        Es la misma persona
                      </Button>
                    )}
                  </div>
                ))}
                <div className="flex flex-wrap gap-2">
                  {candidatos.length === 1 && (
                    <Button type="button" size="sm" onClick={() => usarPersona(candidatos[0])}>
                      Es la misma persona
                    </Button>
                  )}
                  <Button type="button" variant="outline" size="sm" onClick={esOtraPersona}>
                    Es otra
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground border-t pt-2.5">
                  Se liga la persona que ya existe. No se fusionan familias, así que no se
                  mueve ningún saldo ni acceso.
                </p>
              </div>
            )}

            {/* La pregunta 2, y la única puerta a destronar al responsable de una
                familia. Una familia tiene UN responsable: ligar a otro le quita el
                puesto al anterior, y el código de
                consentimiento de un hermano ya registrado se va a un teléfono nuevo.
                Eso no lo decide el sistema. */}
            {conflicto && (
              <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 space-y-3 dark:border-amber-800 dark:bg-amber-950/40">
                <div>
                  <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">
                    Esta familia ya tiene a {conflicto.saliente?.nombre} como responsable.
                  </p>
                  {conflicto.jugadores?.length > 0 && (
                    <p className="text-xs text-amber-800 dark:text-amber-300 mt-0.5">
                      En esta familia: {conflicto.jugadores.join(' · ')}
                    </p>
                  )}
                </div>
                {/* Lo que el admin NO ve si no se lo dicen: que además de reemplazar,
                    está acoplando dos familias. Corregirle el teléfono a esa persona
                    desde Pagos > Familias cambiará las dos. Se queda permitido -- un
                    abuelo responde por nietos en dos hogares -- pero se dice. */}
                {conflicto.familiaDelEntrante && (
                  <p className="text-sm text-amber-900 dark:text-amber-200">
                    {responsableNombre} ya responde por{' '}
                    <strong>{conflicto.familiaDelEntrante}</strong>. Si lo pones aquí, corregir
                    sus datos cambiará las dos.
                  </p>
                )}
                {/* Conservar es la salida esperada y va como primaria; destronar tiene
                    consecuencias (el OTP de los hermanos cambia de teléfono) y va en
                    outline. Con el orden al revés, el botón "por defecto" era el que
                    dejó a dos menores sin su responsable. */}
                <div className="flex flex-wrap gap-2">
                  <Button type="button" size="sm" onClick={dejarAlSaliente}>
                    Dejar a {conflicto.saliente?.nombre}
                  </Button>
                  <Button type="button" variant="outline" size="sm" className="bg-background"
                    onClick={autorizarReemplazo}>
                    Cambiar el responsable a {responsableNombre}
                  </Button>
                </div>
                <p className="text-xs text-amber-700 dark:text-amber-400 border-t border-amber-200 pt-2.5 dark:border-amber-800">
                  Todavía no se guardó nada. Elige una y vuelve a guardar.
                </p>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="tutor_nombres">Nombre(s) *</Label>
                <Input
                  id="tutor_nombres"
                  value={tutorEsJugador ? form.nombres : form.tutor_nombres}
                  onChange={handleNombreResponsable('tutor_nombres')}
                  onBlur={reconocerResponsableDeLaFamilia}
                  placeholder="Mónica"
                  disabled={tutorEsJugador || !!personaLigada}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="tutor_apellidos">Apellidos *</Label>
                <Input
                  id="tutor_apellidos"
                  value={tutorEsJugador ? form.apellidos : form.tutor_apellidos}
                  onChange={handleNombreResponsable('tutor_apellidos')}
                  onBlur={reconocerResponsableDeLaFamilia}
                  placeholder="Paterno y materno"
                  disabled={tutorEsJugador || !!personaLigada}
                />
              </div>

              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="email_tutor">Correo</Label>
                <Input
                  id="email_tutor"
                  value={form.email_tutor}
                  onChange={handleChange('email_tutor')}
                  placeholder="correo@ejemplo.com"
                  type="email"
                  disabled={tutorEsJugador}
                />
              </div>
            </div>

            {/* El nombre de una persona que ya existe se muestra, no se edita: el
                guardado ya no escribe sobre ella. La salida es volver a escribir el
                TELÉFONO, que es lo único que desliga (handleTelefonoResponsable) y
                vuelve a abrir los campos -- el nombre está deshabilitado, así que
                mandar ahí al capturista era mandarlo a un callejón. */}
            {personaLigada ? (
              <div className="space-y-1.5">
                {telefonoNoAplicado && (
                  <p className="text-xs text-amber-700 dark:text-amber-400">
                    El teléfono <strong>{telefonoNoAplicado}</strong> no se va a guardar: se conserva
                    el de {personaLigada.nombre} ({form.tutor_telefono || 'sin teléfono'}). Para
                    cambiárselo, ve a Pagos &gt; Familias.
                    {detenidoPorTelefono && ' Todavía no se guardó nada: vuelve a guardar si está bien.'}
                  </p>
                )}
                <p className="text-xs text-muted-foreground">
                  El responsable es <strong className="text-foreground">{personaLigada.nombre}</strong>,
                  que ya existe en el club. Sus datos se corrigen en Pagos &gt; Familias; para poner a
                  otra persona, vuelve a escribir el teléfono y los campos se desbloquean.
                </p>
              </div>
            ) : menorEdad && (
              <p className="text-xs text-muted-foreground">
                Si no tienes el correo, el tutor lo captura al firmar: ahí se le manda el código
                de verificación. Basta con el teléfono para arrancar.
              </p>
            )}

            {/* La familia no es una sección aparte: es la del responsable. */}
            <div className="space-y-3 border-t pt-4">
              {selectedFamilia ? (
                <div className="flex items-center justify-between p-3 rounded-md border bg-muted/30">
                  <div className="min-w-0">
                    <p className="text-sm font-medium">{selectedFamilia.nombre}</p>
                    <p className="text-xs text-muted-foreground">
                      {selectedFamilia.responsable?.nombre_completo}
                      {selectedFamilia.jugadores?.length > 0 && ` · ${selectedFamilia.jugadores.length} hijo(s)`}
                    </p>
                    {/* Lo que va a pasar, dicho antes de guardar: el mayor que responde
                        por sí mismo entra como hijo, y el responsable de la familia no
                        cambia. Es la pregunta 0 del guardado, en palabras. */}
                    {tutorEsJugador && selectedFamilia.responsable?.nombre_completo
                      && !mismoTexto(selectedFamilia.responsable.nombre_completo, responsableNombre) && (
                      <p className="text-xs text-muted-foreground mt-1.5">
                        {selectedFamilia.responsable.nombre_completo} sigue como responsable de los pagos de esta
                        familia; {form.nombres.trim() || 'el jugador'} firma su propio consentimiento.
                      </p>
                    )}
                  </div>
                  <Button type="button" variant="ghost" size="sm" onClick={() => { guard.markDirty(); setSelectedFamilia(null); setShowFamiliaSearch(true) }}>
                    Cambiar
                  </Button>
                </div>
              ) : (
                <>
                  <div className="rounded-md bg-muted/50 px-3 py-2.5 flex items-start gap-2">
                    <Info className="h-3.5 w-3.5 mt-0.5 shrink-0 text-muted-foreground" />
                    {personaLigada?.familiaNombre ? (
                      <p className="text-xs text-muted-foreground">
                        Se suma a <strong className="text-foreground">{personaLigada.familiaNombre}</strong>,
                        la familia donde {personaLigada.nombre} ya responde. Elige otra abajo si no
                        es ahí donde va.
                      </p>
                    ) : (
                      <p className="text-xs text-muted-foreground">
                        La familia se llamará{' '}
                        <strong className="text-foreground">
                          Familia {apellidoPaterno(responsableApellidos) || '…'}
                        </strong>
                        {' '}— el primer apellido del responsable. Ya no se teclea.
                      </p>
                    )}
                  </div>

                  {/* Sugerencia por apellidos. Agrupar hermanos dependía de que al
                      admin se le ocurriera buscar; en RAIDERS no se le ocurrió 179
                      veces. Aquí se le pone enfrente en cuanto teclea el nombre. */}
                  {familiasHermanas.length > 0 && (
                    <div className="rounded-md border border-primary/30 bg-primary/5 px-3 py-2.5">
                      <p className="text-xs text-muted-foreground mb-2">
                        Hay {familiasHermanas.length === 1 ? 'una familia' : 'familias'} con los mismos
                        apellidos. Si es hermano, súmalo ahí en vez de crear otra.
                      </p>
                      <div className="space-y-1.5">
                        {familiasHermanas.map((f) => (
                          <button
                            key={f.id}
                            type="button"
                            onClick={() => {
                              guard.markDirty()
                              setSelectedFamilia(f)
                              setShowFamiliaSearch(false)
                            }}
                            className="w-full text-left rounded-md border bg-background px-3 py-2 hover:bg-muted"
                          >
                            <span className="block text-sm font-medium">{f.nombre}</span>
                            <span className="block text-xs text-muted-foreground">
                              {(f.jugadores || []).map((j) => j.nombre_completo).join(' · ')}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {showFamiliaSearch ? (
                    <div className="space-y-2">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground h-4 w-4" />
                        <Input
                          placeholder="Buscar familia existente..."
                          value={familiaSearch}
                          onChange={(e) => setFamiliaSearch(e.target.value)}
                          className="pl-10"
                          autoFocus
                        />
                      </div>
                      {familiaSearch && (
                        <div className="space-y-1 max-h-32 overflow-y-auto">
                          {filteredFamilias.map((f) => (
                            <button
                              key={f.id}
                              type="button"
                              className="w-full text-left px-3 py-2 rounded-md hover:bg-muted text-sm flex items-center justify-between"
                              onClick={() => { guard.markDirty(); setSelectedFamilia(f); setShowFamiliaSearch(false); setFamiliaSearch('') }}
                            >
                              <span>{f.nombre}</span>
                              <span className="text-xs text-muted-foreground">{f.responsable?.nombre_completo}</span>
                            </button>
                          ))}
                          {filteredFamilias.length === 0 && (
                            <p className="text-xs text-muted-foreground text-center py-2">No se encontro</p>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <Button type="button" variant="outline" size="sm" onClick={() => setShowFamiliaSearch(true)}>
                      <Search className="h-3.5 w-3.5 mr-1" /> Sumarlo a una familia que ya existe
                    </Button>
                  )}
                </>
              )}
            </div>
          </fieldset>

          <p className="text-xs text-muted-foreground border-t pt-3">
            Los datos se tratan conforme al{' '}
            <LegalDocLink
              doc={renderAvisoClub(org || {})}
              label="Aviso de Privacidad del club"
              className="text-primary underline hover:opacity-80"
            />
            . El consentimiento del tutor se solicita por separado.
          </p>

          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button type="button" variant="outline" onClick={() => guard.onOpenChange(false)} disabled={saving}>
              Cancelar
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? 'Guardando...' : isEditing ? 'Guardar Cambios' : 'Crear Jugador'}
            </Button>
          </div>
        </form>
        {guard.DiscardDialog}

      {(isEditing || pendienteConsent) && (
        <SolicitarConsentimientoDialog
          open={solicitarOpen}
          onOpenChange={setSolicitarOpen}
          jugadorId={player?.id || pendienteConsent?.jugadorId}
          familiaId={player?.familia_id || null}
          // A quien se manda: el mayor de edad firma por si mismo; el menor, su tutor.
          // Lo tecleado como responsable es quien PAGA, y aqui es solo el respaldo.
          tutorTelefono={destinoConsentimiento({
            fechaNacimiento: form.fecha_nacimiento,
            bajoTutela: player?.bajo_tutela_legal,
            telefonoJugador: form.telefono_jugador,
            telefonoTutorLegal: (player?.jugador_personas || []).map((v) => v?.personas?.telefono).find(Boolean),
            telefonoFamilia: selectedFamilia?.responsable?.telefono,
            telefonoContacto: form.tutor_telefono || player?.telefono_contacto,
          }).telefono}
          tituloDestino={nombreCompleto}
          onSolicitado={() => {
            // Solicitar no es firmar. El error rojo invitaba a reintentar el guardado,
            // que iba a fallar igual hasta que el tutor firmara; se cambia por lo que
            // de verdad procede.
            setError(null)
            setConsentErr(false)
            setAvisoConsent(
              'La solicitud quedo lista para enviarse al tutor. Hasta que firme, guarda al jugador como Pendiente.'
            )
          }}
        />
      )}
    </div>
  )
}

export default PlayerForm
